import { pdfjs } from 'react-pdf';

const MAX_SOURCE_IMAGE_PIXELS = 40 * 1024 * 1024;
const MAX_OUTPUT_DIMENSION = 1000;
const IMAGE_DECODE_TIMEOUT_MS = 10000;

const failureMessage = 'Could not extract a usable image from this PDF. Upload a JPG or PNG instead.';

const logDiagnostic = (event, details) => {
  if (process.env.NODE_ENV !== 'production') {
    // Keep PDF diagnostics local to development; never log image bytes.
    console.info('[Candidate photo PDF extraction]', event, details);
  }
};

const configurePdfWorker = () => {
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
  }
};

const isValidDimension = (value) => Number.isFinite(value) && value > 0;

const getImageArea = (imageData) => {
  const width = Number(imageData?.width);
  const height = Number(imageData?.height);

  return isValidDimension(width) && isValidDimension(height) ? width * height : 0;
};

const getPdfImageObject = (page, objectId) => {
  // Operator lists may arrive before the worker finishes decoding their images.
  // PDF.js stores cross-page images in commonObjs, just as its renderer does.
  const objects = objectId.startsWith('g_') ? page.commonObjs : page.objs;

  return new Promise((resolve) => {
    let isSettled = false;
    const finish = (imageData) => {
      if (isSettled) {
        return;
      }
      isSettled = true;
      clearTimeout(timeout);
      resolve(imageData);
    };
    const timeout = setTimeout(() => {
      logDiagnostic('image-object-unavailable', { objectId, reason: 'decode-timeout' });
      finish(null);
    }, IMAGE_DECODE_TIMEOUT_MS);

    try {
      objects.get(objectId, finish);
    } catch (error) {
      logDiagnostic('image-object-unavailable', { objectId, reason: error.message });
      finish(null);
    }
  });
};

const getPageImageCandidates = async (page) => {
  const operatorList = await page.getOperatorList();
  const seenObjectIds = new Set();

  const candidates = await Promise.all(
    operatorList.fnArray.map(async (operator, index) => {
      const args = operatorList.argsArray[index] || [];

      if (operator === pdfjs.OPS.paintImageXObject || operator === pdfjs.OPS.paintImageXObjectRepeat) {
        const objectId = args[0];
        if (seenObjectIds.has(objectId)) {
          return null;
        }

        seenObjectIds.add(objectId);
        const imageData = await getPdfImageObject(page, objectId);
        return imageData ? { imageData, objectId } : null;
      }

      // Inline images are already decoded data rather than page object references.
      if (operator === pdfjs.OPS.paintInlineImageXObject && args[0]) {
        return { imageData: args[0], objectId: `inline-${index}` };
      }
      return null;
    }),
  );

  return candidates.filter(Boolean).filter(({ imageData }) => {
    const area = getImageArea(imageData);
    const isWithinMemoryLimit = area > 0 && area <= MAX_SOURCE_IMAGE_PIXELS;

    if (!isWithinMemoryLimit && area > 0) {
      logDiagnostic('image-skipped', {
        height: imageData.height,
        reason: 'source-image-too-large',
        width: imageData.width,
      });
    }

    return isWithinMemoryLimit;
  });
};

const getOutputDimensions = (width, height) => {
  const scale = Math.min(1, MAX_OUTPUT_DIMENSION / Math.max(width, height));

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const normalizeImagePixels = (imageData) => {
  const width = Number(imageData.width);
  const height = Number(imageData.height);
  const pixels = width * height;
  const source = imageData.data;

  if (!source || !pixels) {
    throw new Error('PDF.js did not provide decoded image pixels.');
  }

  if (imageData.kind === pdfjs.ImageKind.GRAYSCALE_1BPP) {
    const bytesPerRow = Math.ceil(width / 8);
    if (source.length !== bytesPerRow * height) {
      throw new Error('PDF grayscale image pixels are incomplete.');
    }
    const rgba = new Uint8ClampedArray(pixels * 4);
    for (let row = 0; row < height; row += 1) {
      for (let column = 0; column < width; column += 1) {
        const sourceByte = source[row * bytesPerRow + (column >> 3)];
        const value = sourceByte & (128 >> column % 8) ? 255 : 0;
        const targetIndex = (row * width + column) * 4;
        rgba[targetIndex] = value;
        rgba[targetIndex + 1] = value;
        rgba[targetIndex + 2] = value;
        rgba[targetIndex + 3] = 255;
      }
    }
    return rgba;
  }

  if (source.length === pixels * 4) {
    return new Uint8ClampedArray(source);
  }

  const rgba = new Uint8ClampedArray(pixels * 4);

  if (source.length === pixels * 3) {
    for (let sourceIndex = 0, targetIndex = 0; sourceIndex < source.length; sourceIndex += 3, targetIndex += 4) {
      rgba[targetIndex] = source[sourceIndex];
      rgba[targetIndex + 1] = source[sourceIndex + 1];
      rgba[targetIndex + 2] = source[sourceIndex + 2];
      rgba[targetIndex + 3] = 255;
    }
    return rgba;
  }

  if (source.length === pixels) {
    for (let sourceIndex = 0, targetIndex = 0; sourceIndex < source.length; sourceIndex += 1, targetIndex += 4) {
      rgba[targetIndex] = source[sourceIndex];
      rgba[targetIndex + 1] = source[sourceIndex];
      rgba[targetIndex + 2] = source[sourceIndex];
      rgba[targetIndex + 3] = 255;
    }
    return rgba;
  }

  throw new Error('PDF image pixels use an unsupported format.');
};

const canvasToJpeg = (canvas) => {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('The browser could not encode the extracted image.'));
          return;
        }

        resolve(blob);
      },
      'image/jpeg',
      0.9,
    );
  });
};

const convertImageDataToJpeg = async (imageData) => {
  const sourceWidth = Number(imageData.width);
  const sourceHeight = Number(imageData.height);
  const { width, height } = getOutputDimensions(sourceWidth, sourceHeight);
  const outputCanvas = document.createElement('canvas');
  const outputContext = outputCanvas.getContext('2d');

  outputCanvas.width = width;
  outputCanvas.height = height;
  outputContext.fillStyle = '#FFFFFF';
  outputContext.fillRect(0, 0, width, height);

  if (imageData.bitmap) {
    outputContext.drawImage(imageData.bitmap, 0, 0, width, height);
  } else {
    const sourceCanvas = document.createElement('canvas');
    const sourceContext = sourceCanvas.getContext('2d');

    sourceCanvas.width = sourceWidth;
    sourceCanvas.height = sourceHeight;
    const sourceImage = sourceContext.createImageData(sourceWidth, sourceHeight);
    sourceImage.data.set(normalizeImagePixels(imageData));
    sourceContext.putImageData(sourceImage, 0, 0);
    outputContext.drawImage(sourceCanvas, 0, 0, width, height);
    sourceCanvas.remove();
  }

  const jpeg = await canvasToJpeg(outputCanvas);
  outputCanvas.remove();
  return jpeg;
};

export const isPdfCandidatePhoto = (file) => {
  return file?.type === 'application/pdf' || /\.pdf$/i.test(file?.name || '');
};

export const createCandidatePhotoMatch = (matchedFile, image) => {
  return {
    ...matchedFile,
    file: new File([image], matchedFile.file.name.replace(/\.pdf$/i, '.jpg'), { type: 'image/jpeg' }),
    isFailedImageExtraction: false,
  };
};

export const extractCandidatePhotoFromPdf = async (file) => {
  let loadingTask;
  let documentProxy;

  try {
    configurePdfWorker();
    const pdfBytes = new Uint8Array(await file.arrayBuffer());
    loadingTask = pdfjs.getDocument({ data: pdfBytes });
    documentProxy = await loadingTask.promise;
    const candidates = [];
    const pageCount = documentProxy.numPages;

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber += 1) {
      const page = await documentProxy.getPage(pageNumber);
      const pageCandidates = await getPageImageCandidates(page);

      logDiagnostic('page-scanned', {
        fileName: file.name,
        imageCount: pageCandidates.length,
        pageNumber,
      });

      pageCandidates.forEach((candidate) => {
        candidates.push({ ...candidate, pageNumber, area: getImageArea(candidate.imageData) });
      });
    }

    const selectedImage = candidates.sort((first, second) => second.area - first.area)[0];

    if (!selectedImage) {
      logDiagnostic('no-usable-image', { fileName: file.name, pageCount });
      return { ok: false, error: failureMessage, reason: 'no_usable_image' };
    }

    const image = await convertImageDataToJpeg(selectedImage.imageData);
    logDiagnostic('image-extracted', {
      fileName: file.name,
      height: selectedImage.imageData.height,
      pageNumber: selectedImage.pageNumber,
      width: selectedImage.imageData.width,
    });

    return { ok: true, image };
  } catch (error) {
    logDiagnostic('extraction-failed', {
      fileName: file?.name,
      reason: error?.message || 'unknown-error',
    });
    return { ok: false, error: failureMessage, reason: 'pdf_decode_failed' };
  } finally {
    try {
      if (documentProxy) {
        await documentProxy.destroy();
      } else if (loadingTask) {
        await loadingTask.destroy();
      }
    } catch (error) {
      logDiagnostic('cleanup-failed', {
        fileName: file?.name,
        reason: error?.message || 'unknown-error',
      });
    }
  }
};

export const candidatePhotoExtractionFailureMessage = failureMessage;

// Both automatic matching and manual assignment retain the original on failure.
export const prepareCandidatePhotoMatch = async (matchedFile) => {
  if (!isPdfCandidatePhoto(matchedFile.file)) {
    return { ok: true, file: matchedFile };
  }
  const extraction = await extractCandidatePhotoFromPdf(matchedFile.file);
  if (!extraction.ok) {
    return { ...extraction, file: matchedFile, ok: false };
  }
  return { ok: true, file: createCandidatePhotoMatch(matchedFile, extraction.image) };
};
